import CloseIcon from '@mui/icons-material/Close';
import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	FormLabel,
	IconButton,
	Link,
	Radio,
	RadioGroup,
	Typography,
} from '@mui/material';
import React, { type ChangeEvent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAccount } from '../../../redux/slices/accountSlice';
import { showAlert } from '../../../redux/slices/alertSlice';
import { UPLOAD_NLA_SUCCESS } from '../../../constants/messages';
import { useUploadNonLeasedAssetsMutation } from '../../../api/asset.api';

interface UploadNonLeasedAssetsModalProps {
	open: boolean;
	onClose: () => void;
	onUploadSuccess?: () => void;
}

const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;

const UploadNonLeasedAssetsModal: React.FC<UploadNonLeasedAssetsModalProps> = ({
	open,
	onClose,
	onUploadSuccess,
}) => {
	const [uploadType, setUploadType] = useState<'add' | 'edit'>('add');
	const [file, setFile] = useState<File | null>(null);
	const [error, setError] = useState<string | null>(null);
	const account = useSelector(selectAccount);
	const [uploadNonLeasedAssets, { isLoading }] = useUploadNonLeasedAssetsMutation();

	const dispatch = useDispatch();

	const handleUploadTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setUploadType(event.target.value as 'add' | 'edit');
	};

	const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
		setError(null);
		const selectedFile = event.target.files?.[0];
		if (!selectedFile) return;
		if (!selectedFile.name.endsWith('.csv')) {
			setError('File must be a .csv file.');
			setFile(null);
			return;
		}
		if (selectedFile.size > MAX_FILE_SIZE) {
			setError(`File size must be less than ${MAX_FILE_SIZE_MB}MB.`);
			setFile(null);
			return;
		}
		// Add more file name checks if needed
		setFile(selectedFile);
	};

	const resetModalState = () => {
		setUploadType('add');
		setFile(null);
		setError(null);
	};

	const handleClose = () => {
		resetModalState();
		onClose();
	};

	const handleUpload = async () => {
		setError(null);
		if (!file) {
			setError('Please select a file to upload.');
			return;
		}
		try {
			const result = await uploadNonLeasedAssets({
				file: file,
				uploadType: uploadType,
				sourceId: account.sourceId,
			}).unwrap();
			if (!result.success) {
				const allErrors = result.errors
					.map((e) => `Row ${e.row}, Column "${e.column}": ${e.message}`)
					.join('; \n\n');
				throw new Error(allErrors || 'Upload failed.');
			}
			dispatch(
				showAlert({
					message: UPLOAD_NLA_SUCCESS,
					type: 'success',
				})
			);
			if (onUploadSuccess) {
				onUploadSuccess();
			}
			handleClose();
		} catch (err: any) {
			setError(err.message || 'Failed to upload file.');
		}
	};

	if (!open) return null;

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="xs"
			fullWidth
			data-testid="upload-nla-modal"
		>
			<DialogTitle
				sx={{
					backgroundColor: (theme) => theme.palette.surface.tertiary,
					pr: 5,
					position: 'relative',
				}}
			>
				<span>Upload File - Non-Leased Assets</span>
				<IconButton
					aria-label="close"
					onClick={handleClose}
					sx={{
						position: 'absolute',
						top: '50%',
						right: 16,
						transform: 'translateY(-50%)',
						border: '2px solid',
						borderColor: (theme) => theme.palette.brand.border,
						color: (theme) => theme.palette.brand.border,
						borderRadius: '50%',
						p: 0.5,
						backgroundColor: 'background.paper',
						'&:hover': {
							backgroundColor: 'grey.100',
						},
					}}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent>
				<Box sx={{ mt: 2 }}>
					<FormLabel id="upload-type-label" sx={{ mt: 1 }}>
						Upload Type
					</FormLabel>
					<RadioGroup
						aria-labelledby="upload-type-label"
						name="upload-type-group"
						value={uploadType}
						onChange={handleUploadTypeChange}
						sx={{ mb: 2 }}
					>
						<FormControlLabel value="add" control={<Radio />} label="Add Assets" />
						<FormControlLabel value="edit" control={<Radio />} label="Edit Assets" />
					</RadioGroup>
				</Box>
				<Box sx={{ mb: 1 }}>
					<input
						id="upload-non-leased-assets-file-input"
						type="file"
						accept=".csv"
						style={{ display: 'none' }}
						onChange={handleFileChange}
					/>
					<label htmlFor="upload-non-leased-assets-file-input">
						<Link
							component="span"
							variant="button"
							sx={{
								cursor: 'pointer',
								textTransform: 'none',
								p: 0,
								minWidth: 0,
								color: (theme) => theme.palette.brand.primary,
							}}
							onClick={() => {setError(null)}}
							underline="hover"
						>
							Select File &gt;
						</Link>
						{file && (
							<Typography variant="body2" sx={{ ml: 1, display: 'inline' }}>
								{file.name}
							</Typography>
						)}
					</label>
				</Box>
				<Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
					Note: files must be in .csv format
				</Typography>
				{error && (
					<Alert severity="error" sx={{ mb: 1 }}>
						<Box sx={{ whiteSpace: 'pre-line' }}>
							{error}
						</Box>
					</Alert>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={isLoading}>
					Cancel
				</Button>
				<Button variant="contained" onClick={handleUpload} disabled={isLoading || !file}>
					Import
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default UploadNonLeasedAssetsModal;
